import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { NewDeal } from '../api/types'
import { LOAN_TYPES, PURPOSES } from '../lib/constants'
import { Modal } from './Modal'
import { useToast } from './Toast'

/** A loan opportunity before LendingPad has a file: pre-approval conversations,
 *  a referral that hasn't applied. It sits on the pipeline board like any loan and
 *  links to the LOS loan once one exists. */
export function NewDealModal({ open, onClose, contactId, borrowerName, stages, initialStage, onCreated }: {
  open: boolean
  onClose: () => void
  contactId?: number
  borrowerName?: string
  stages?: { key: string; label: string }[]
  initialStage?: string
  onCreated?: (loanId: string) => void
}) {
  const blank = (): NewDeal => ({
    borrower_name: borrowerName ?? '', purpose: 'purchase', loan_type: 'conventional',
    loan_amount: '', purchase_price: '', note_rate: '', property_street: '', property_city: '',
    property_state: '', property_zip: '', stage: initialStage ?? 'lead', notes: '',
  })
  const [form, setForm] = useState<NewDeal>(blank)
  // Re-seed on every open: the board's per-column "+" passes a different stage each time.
  useEffect(() => {
    if (open) setForm(blank())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialStage, borrowerName])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const nav = useNavigate()
  const toast = useToast()

  const set = (k: keyof NewDeal) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const close = () => {
    setForm(blank())
    setError('')
    onClose()
  }

  const canSave = Boolean((form.borrower_name ?? '').trim() || contactId) && !busy

  async function save() {
    if (!canSave) return
    setBusy(true)
    setError('')
    try {
      const num = (v: unknown) => (v === '' || v === undefined ? undefined : Number(String(v).replace(/[$,\s%]/g, '')))
      const payload: NewDeal = {
        ...form,
        contact_id: contactId,
        loan_amount: num(form.loan_amount),
        purchase_price: num(form.purchase_price),
        note_rate: num(form.note_rate),
        stage: stages?.length ? form.stage : undefined,
      }
      const { loan_id } = await api.createDeal(payload)
      toast('Opportunity created', { tone: 'positive' })
      close()
      if (onCreated) onCreated(loan_id)
      else nav(`/loans/${encodeURIComponent(loan_id)}`)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="New loan opportunity"
      subtitle="Tracked in the CRM until the loan system opens a file for it."
      width={580}
      footer={
        <>
          <button className="btn" onClick={close}>Cancel</button>
          <button className="btn primary" onClick={save} disabled={!canSave}>{busy ? 'Saving…' : 'Create'}</button>
        </>
      }
    >
      <form className="form-grid" onSubmit={(e) => { e.preventDefault(); save() }}>
        <label className="fl span-2"><span>Borrower</span>
          <input className="field" value={form.borrower_name} onChange={set('borrower_name')} disabled={Boolean(contactId && borrowerName)} /></label>
        <label className="fl"><span>Purpose</span>
          <select className="field" value={form.purpose} onChange={set('purpose')}>
            {PURPOSES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select></label>
        <label className="fl"><span>Loan type</span>
          <select className="field" value={form.loan_type} onChange={set('loan_type')}>
            {LOAN_TYPES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select></label>
        <label className="fl"><span>Loan amount</span>
          <input className="field mono" inputMode="decimal" placeholder="$" value={form.loan_amount} onChange={set('loan_amount')} /></label>
        <label className="fl"><span>{form.purpose === 'purchase' ? 'Purchase price' : 'Estimated value'}</span>
          <input className="field mono" inputMode="decimal" placeholder="$" value={form.purchase_price} onChange={set('purchase_price')} /></label>
        <label className="fl"><span>Rate <span className="hint">— if quoted</span></span>
          <input className="field mono" inputMode="decimal" placeholder="%" value={form.note_rate} onChange={set('note_rate')} /></label>
        {stages?.length ? (
          <label className="fl"><span>Stage</span>
            <select className="field" value={form.stage} onChange={set('stage')}>
              {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select></label>
        ) : <span />}
        <label className="fl span-2"><span>Property address</span>
          <input className="field" value={form.property_street} onChange={set('property_street')} placeholder="Street, or leave blank if still shopping" /></label>
        <div className="span-2" style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.5fr 0.8fr', gap: 10 }}>
          <label className="fl"><span>City</span><input className="field" value={form.property_city} onChange={set('property_city')} /></label>
          <label className="fl"><span>State</span><input className="field" value={form.property_state} onChange={set('property_state')} maxLength={2} /></label>
          <label className="fl"><span>ZIP</span><input className="field" value={form.property_zip} onChange={set('property_zip')} maxLength={10} /></label>
        </div>
        <label className="fl span-2"><span>Notes</span>
          <textarea className="field" rows={2} value={form.notes} onChange={set('notes')} style={{ minHeight: 56 }} /></label>
        {error ? <div className="notice danger span-2"><span>{error}</span></div> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}
